import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import Avatar from "./Avatar";
import ConfirmDialog from "./ConfirmDialog";
import CloseIcon from "./icons";

function Msg({ msg }) {
  if (!msg) return null;
  return (
    <div
      className={`mt-2 text-xs rounded-lg px-3 py-2 border ${
        msg.type === "error"
          ? "text-red-300 bg-red-500/10 border-red-500/30"
          : "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
      }`}
    >
      {msg.text}
    </div>
  );
}

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-100 " +
  "placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700 transition";
const saveCls =
  "px-4 py-2 rounded-lg bg-emerald-500 text-zinc-950 font-semibold text-sm " +
  "hover:bg-emerald-400 transition disabled:opacity-50 disabled:cursor-not-allowed";
const labelCls = "text-[10px] uppercase tracking-widest text-zinc-500";

export default function EditProfileModal({ open, onClose }) {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarMsg, setAvatarMsg] = useState(null);

  const [username, setUsername] = useState(profile?.username || "");
  const [usernameBusy, setUsernameBusy] = useState(false);
  const [usernameMsg, setUsernameMsg] = useState(null);

  const [email, setEmail] = useState(user?.email || "");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);

  const [password, setPassword] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!open || !user) return null;

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setAvatarMsg({ type: "error", text: "Please choose an image file" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarMsg({ type: "error", text: "Image must be under 5 MB" });
      return;
    }
    setAvatarBusy(true);
    setAvatarMsg(null);
    try {
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const path = `${user.id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("avatars")
        .upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const {
        data: { publicUrl },
      } = supabase.storage.from("avatars").getPublicUrl(path);
      const { error: dbErr } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);
      if (dbErr) throw dbErr;
      await refreshProfile();
      setAvatarMsg({ type: "success", text: "Avatar updated" });
    } catch (err) {
      setAvatarMsg({ type: "error", text: err.message || "Upload failed" });
    } finally {
      setAvatarBusy(false);
    }
  };

  const saveUsername = async () => {
    if (!/^[a-zA-Z0-9_-]{3,20}$/.test(username)) {
      setUsernameMsg({
        type: "error",
        text: "3–20 chars, letters/numbers/underscore/hyphen",
      });
      return;
    }
    setUsernameBusy(true);
    setUsernameMsg(null);
    const { error } = await supabase
      .from("profiles")
      .update({ username })
      .eq("id", user.id);
    if (error) {
      setUsernameMsg({
        type: "error",
        text: error.code === "23505" ? "Username already taken" : error.message,
      });
    } else {
      await refreshProfile();
      setUsernameMsg({ type: "success", text: "Username saved" });
    }
    setUsernameBusy(false);
  };

  const saveEmail = async () => {
    setEmailBusy(true);
    setEmailMsg(null);
    const { error } = await supabase.auth.updateUser({ email });
    if (error) setEmailMsg({ type: "error", text: error.message });
    else
      setEmailMsg({
        type: "success",
        text: "Confirmation link sent to the new address",
      });
    setEmailBusy(false);
  };

  const savePassword = async () => {
    if (password.length < 6) {
      setPasswordMsg({ type: "error", text: "Min 6 characters" });
      return;
    }
    setPasswordBusy(true);
    setPasswordMsg(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) setPasswordMsg({ type: "error", text: error.message });
    else {
      setPassword("");
      setPasswordMsg({ type: "success", text: "Password updated" });
    }
    setPasswordBusy(false);
  };

  const deleteAccount = async () => {
    setConfirmingDelete(false);
    setDeleting(true);
    const { error } = await supabase.rpc("delete_own_account");
    if (error) {
      setDeleting(false);
      return;
    }
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-md grid place-items-center p-6"
        onClick={onClose}
      >
        <div
          className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-md
                     max-h-[92vh] overflow-y-auto scrollbar-fancy p-7
                     animate-scale-in shadow-2xl shadow-black/50 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-zinc-100">Edit profile</h3>
            <button
              onClick={onClose}
              className="w-8 h-8 grid place-items-center rounded-full text-zinc-500
                         hover:text-zinc-100 hover:bg-zinc-900 transition"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Avatar */}
          <div className="flex items-center gap-4 mb-7">
            <Avatar
              username={profile?.username}
              email={user.email}
              url={profile?.avatar_url}
              size={64}
            />
            <div>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={avatarBusy}
                className="px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm
                           font-semibold text-zinc-200 hover:border-zinc-700 transition
                           disabled:opacity-50"
              >
                {avatarBusy ? "Uploading…" : "Change avatar"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatar}
              />
              <Msg msg={avatarMsg} />
            </div>
          </div>

          {/* Username */}
          <div className="mb-6">
            <span className={labelCls}>Username</span>
            <div className="mt-1 flex gap-2">
              <input
                className={inputCls}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <button
                className={saveCls}
                onClick={saveUsername}
                disabled={usernameBusy}
              >
                Save
              </button>
            </div>
            <Msg msg={usernameMsg} />
          </div>

          {/* Email */}
          <div className="mb-6">
            <span className={labelCls}>Email</span>
            <div className="mt-1 flex gap-2">
              <input
                type="email"
                className={inputCls}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                className={saveCls}
                onClick={saveEmail}
                disabled={emailBusy}
              >
                Save
              </button>
            </div>
            <Msg msg={emailMsg} />
          </div>

          {/* Password */}
          <div className="mb-7">
            <span className={labelCls}>New password</span>
            <div className="mt-1 flex gap-2">
              <input
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className={inputCls}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                className={saveCls}
                onClick={savePassword}
                disabled={passwordBusy}
              >
                Save
              </button>
            </div>
            <Msg msg={passwordMsg} />
          </div>

          {/* Danger zone */}
          <div className="pt-5 border-t border-zinc-800">
            {/* <span className={labelCls}>Danger zone</span> */}
            <button
              onClick={() => setConfirmingDelete(true)}
              disabled={deleting}
              className="mt-2 w-full py-2.5 rounded-lg bg-red-500/10 border border-red-500/40
                         text-sm font-semibold text-red-300 hover:bg-red-500/20 transition
                         disabled:opacity-50"
            >
              {deleting ? "Deleting…" : "Delete account"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title="Delete your account?"
        description="This permanently removes your account, profile, runs, and ratings. This cannot be undone."
        confirmLabel="Delete forever"
        destructive
        onConfirm={deleteAccount}
        onCancel={() => setConfirmingDelete(false)}
      />
    </>
  );
}
